/* Testing a proposed rules package (REP-08). Every colour is a token (ADR-0007, stylelint). */

/* The verdict. Named after what was done, so the two states are "would be refused" and "would not"
   rather than a tick and a cross — and both carry a word, because colour never carries meaning on
   its own (design rule 8). */
.verdict {
  padding: 8px 10px;
  border-radius: 6px;
  font-size: var(--t-min);
  font-weight: 600;
  margin-bottom: 10px;
}

/* `--ok` rather than `--accent`: the accent is this product's action colour and sits under every
   primary button on the screen, so a passing check painted in it reads as something to press. */
.verdictPassed {
  background: var(--ok-w);
  border: 1px solid var(--ok-line);
  color: var(--ok);
}

.verdictFailed {
  background: var(--crit-w);
  border: 1px solid var(--crit-line);
  color: var(--crit);
}

.verdictSub {
  display: block;
  margin-top: 3px;
  font-weight: 400;
  line-height: 1.5;
  /* The sentence carries a version string, and a version string is one unbreakable token on a
     400px screen. */
  overflow-wrap: anywhere;
}

/* One group of changes of one kind. */
.group {
  display: grid;
  gap: 3px;
  margin-top: 10px;
  padding: 8px 10px;
  border: 1px solid var(--line);
  border-radius: 7px;
  background: var(--panel-2);
}

/* **`recited` does not look like the others, and that is the requirement rather than the style.**
   A changed citation under an unchanged sentence is the edit a reviewer skims past; a row in the
   same grey box as "a label was reworded" is exactly how it gets skimmed. A left bar, the warning
   surface and a heavier heading — on top of the word "citation" in the heading itself, because the
   treatment must survive a reader who cannot see the colour. */
.groupRecited {
  border-color: var(--warn-line);
  border-left: 3px solid var(--warn);
  background: var(--warn-w);
}

.groupRecited > .groupHeading {
  color: var(--warn);
  font-size: var(--t-dense);
}

.groupHeading {
  font-size: var(--t-min);
  font-weight: 700;
  color: var(--ink);
}

/* A count beside its heading. Separate per group and per list, never summed: two facts added
   together are one number that answers neither question. */
.groupCount,
.keyCount {
  display: inline-block;
  min-width: 16px;
  padding: 0 5px;
  border-radius: 9px;
  background: var(--panel-3);
  color: var(--ink-2);
  font-size: var(--t-min);
  font-weight: 700;
  font-variant-numeric: lining-nums tabular-nums;
  text-align: center;
}

.groupWhy,
.keyWhy {
  font-size: var(--t-min);
  line-height: 1.55;
  color: var(--ink-2);
}

.changes,
.keys {
  list-style: none;
  margin: 4px 0 0;
  padding: 0;
  display: grid;
  gap: 4px;
}

.change {
  display: grid;
  gap: 1px;
}

/* A disclosure key is a machine name a reader will search the source tree for, so it is set in the
   mono face and allowed to break — `secr_aggregate_energy_kwh` is wider than a 400px column. */
.changeKey,
.keys > li {
  font-family: var(--mono);
  font-size: var(--t-min);
  color: var(--ink);
  overflow-wrap: anywhere;
}

/* The old and new citation, which is the content of a `recited` row. Allowed to wrap anywhere for
   the same reason the key is: a paragraph reference is not a word a browser knows how to break. */
.changeDetail {
  font-size: var(--t-min);
  line-height: 1.5;
  color: var(--ink-2);
  overflow-wrap: anywhere;
}

/* What is wrong with the package on its own terms. Blocking and warning must not look alike: one
   refuses the load and the other is read. */
.findings {
  display: grid;
  gap: 7px;
  margin-bottom: 10px;
}

.finding {
  display: flex;
  gap: 8px;
  align-items: flex-start;
}

.mark {
  flex: 0 0 auto;
  width: 6px;
  height: 6px;
  margin-top: 5px;
  border-radius: 50%;
}

.markBlocking {
  background: var(--crit);
}

.markWarning {
  background: var(--warn);
}

.findingKey {
  font-family: var(--mono);
  font-size: var(--t-min);
  color: var(--ink);
  overflow-wrap: anywhere;
}

/* The severity as a word. The dot above is emphasis; this is the signal. */
.severity {
  display: inline-block;
  padding: 0 5px;
  border-radius: 3px;
  background: var(--panel-3);
  color: var(--ink-2);
  font-size: var(--t-min);
  font-weight: 700;
}

.findingWhat {
  display: block;
  margin-top: 2px;
  font-size: var(--t-min);
  line-height: 1.55;
  color: var(--ink-2);
}

/* The two lists that must never become one — lines that produced nothing, and lines nothing
   exercised. Same shape deliberately, because they are the same shape; the heading and the
   sentence are what tell them apart, and the sentence is not optional. */
.keyBlock {
  display: grid;
  gap: 3px;
  margin-top: 10px;
  padding: 8px 10px;
  border: 1px solid var(--line);
  border-left: 3px solid var(--info-line);
  border-radius: 0 7px 7px 0;
  background: var(--info-w);
}

.keyHeading {
  font-size: var(--t-min);
  font-weight: 700;
  color: var(--info);
}

.runRow {
  display: flex;
  align-items: center;
  gap: 12px;
  margin: 12px 0 0;
}
